export interface MetaPixelProductEventParams {
    content_ids: string[];
    content_name?: string;
    content_category?: string;
    value?: number;
    currency?: string;
    num_items?: number;
    [key: string]: any;
}

export interface MetaPixelPurchaseParams {
    orderId: string;
    content_ids: string[];
    value: number;
    currency?: string;
    num_items?: number;
    [key: string]: any;
}

declare global {
    interface Window {
        fbq?: (...args: any[]) => void;
        _fbq?: any;
        __meta_pixel_initialized__?: boolean;
    }
}

// In-memory queue for events triggered before window.fbq is initialized
interface QueuedEvent {
    method: 'track' | 'trackCustom';
    eventName: string;
    params?: Record<string, any>;
}

const pendingQueue: QueuedEvent[] = [];
let isQueueFlushing = false;

export const flushMetaPixelQueue = () => {
    if (!process.client || typeof window === 'undefined' || typeof window.fbq !== 'function') {
        return;
    }
    if (isQueueFlushing) return;
    isQueueFlushing = true;
    try {
        while (pendingQueue.length > 0) {
            const item = pendingQueue.shift();
            if (item) {
                try {
                    if (item.params) {
                        window.fbq(item.method, item.eventName, item.params);
                        console.log(`[MetaPixel] Flushed queued ${item.method}("${item.eventName}"):`, item.params);
                    } else {
                        window.fbq(item.method, item.eventName);
                        console.log(`[MetaPixel] Flushed queued ${item.method}("${item.eventName}")`);
                    }
                } catch (e) {
                    console.error(`[MetaPixel] Error flushing queued ${item.eventName}:`, e);
                }
            }
        }
    } finally {
        isQueueFlushing = false;
    }
};

// Privacy sanitizer: strip any customer PII / sensitive fields before sending
const PII_KEYWORDS = [
    'email', 'mail', 'phone', 'mobile', 'tel', 'cell',
    'password', 'pass', 'token', 'secret',
    'address', 'shortaddress', 'billing_address', 'shipping_address',
    'fullname', 'firstname', 'lastname', 'username', 'customername',
    'txid', 'transaction_id', 'bankaccount', 'account', 'slip', 'image', 'attachment'
];

const sanitizeParams = (params: Record<string, any>): Record<string, any> => {
    const sanitized: Record<string, any> = {};
    for (const [key, val] of Object.entries(params)) {
        const lowerKey = key.toLowerCase();
        if (PII_KEYWORDS.some(pii => lowerKey.includes(pii))) {
            continue;
        }
        sanitized[key] = val;
    }
    return sanitized;
};

// Validation helpers
const validateContentIds = (contentIds?: any[]): string[] => {
    if (!contentIds || !Array.isArray(contentIds)) return [];
    return contentIds
        .map(id => String(id || '').trim())
        .filter(id => id.length > 0);
};

const validateNumericValue = (val: any): number | null => {
    if (typeof val === 'number') {
        return !isNaN(val) && isFinite(val) ? Number(val.toFixed(2)) : null;
    }
    if (typeof val === 'string') {
        const parsed = parseFloat(val);
        return !isNaN(parsed) && isFinite(parsed) ? Number(parsed.toFixed(2)) : null;
    }
    return null;
};

// Bounded, expiry-based order storage for Purchase deduplication
const PURCHASE_STORAGE_KEY = 'meta_pixel_purchases_v1';
const MAX_PURCHASE_HISTORY = 50;
const PURCHASE_EXPIRY_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

interface PurchaseRecord {
    id: string;
    timestamp: number;
}

const getRecordedPurchases = (): PurchaseRecord[] => {
    if (!process.client || typeof window === 'undefined') return [];
    try {
        const raw = localStorage.getItem(PURCHASE_STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        const now = Date.now();
        return parsed.filter(entry =>
            entry &&
            typeof entry.id === 'string' &&
            typeof entry.timestamp === 'number' &&
            now - entry.timestamp < PURCHASE_EXPIRY_MS
        );
    } catch (e) {
        return [];
    }
};

const isOrderRecorded = (orderId: string): boolean => {
    const records = getRecordedPurchases();
    return records.some(entry => entry.id === orderId);
};

const recordPurchaseSuccess = (orderId: string): void => {
    if (!process.client || typeof window === 'undefined') return;
    try {
        let records = getRecordedPurchases();
        if (!records.some(entry => entry.id === orderId)) {
            records.push({ id: orderId, timestamp: Date.now() });
            if (records.length > MAX_PURCHASE_HISTORY) {
                records = records.slice(records.length - MAX_PURCHASE_HISTORY);
            }
            localStorage.setItem(PURCHASE_STORAGE_KEY, JSON.stringify(records));
        }
    } catch (e) {}
};

// Short-term deduplication trackers to prevent rapid duplicate watcher/mount fires
let lastViewContentCall = { id: '', timestamp: 0 };
let lastSearchCall = { query: '', timestamp: 0 };
let lastCheckoutCall = { key: '', timestamp: 0 };

export const useMetaPixel = () => {
    const isClient = process.client && typeof window !== 'undefined';

    const dispatch = (method: 'track' | 'trackCustom', eventName: string, params?: Record<string, any>) => {
        if (!isClient) return;

        const cleanParams = params ? sanitizeParams(params) : undefined;

        if (typeof window.fbq === 'function') {
            flushMetaPixelQueue();
            try {
                if (cleanParams) {
                    window.fbq(method, eventName, cleanParams);
                    console.log(`[MetaPixel] Dispatched ${method}("${eventName}"):`, cleanParams);
                } else {
                    window.fbq(method, eventName);
                    console.log(`[MetaPixel] Dispatched ${method}("${eventName}")`);
                }
            } catch (err) {
                console.error(`[MetaPixel] Error dispatching ${eventName}:`, err);
            }
        } else {
            console.log(`[MetaPixel] Queuing ${method}("${eventName}"):`, cleanParams);
            pendingQueue.push({ method, eventName, params: cleanParams });
        }
    };

    const track = (eventName: string, params?: Record<string, any>) => {
        dispatch('track', eventName, params);
    };

    const trackCustom = (eventName: string, params?: Record<string, any>) => {
        dispatch('trackCustom', eventName, params);
    };

    const trackPageView = () => {
        track('PageView');
    };

    const trackViewContent = (params: MetaPixelProductEventParams) => {
        const validIds = validateContentIds(params?.content_ids);
        if (validIds.length === 0) return;

        const currentId = validIds[0];
        const now = Date.now();
        // Short debounce (1000ms) only for identical product ID to handle multiple watcher triggers on a single page view
        // Legitimate repeated views (browsing back to product later) are fully preserved.
        if (lastViewContentCall.id === currentId && now - lastViewContentCall.timestamp < 1000) {
            return;
        }
        lastViewContentCall = { id: currentId, timestamp: now };

        const numericValue = validateNumericValue(params?.value);

        track('ViewContent', {
            content_ids: validIds,
            content_type: 'product',
            content_name: params.content_name || '',
            value: numericValue !== null ? numericValue : 0,
            currency: (params.currency && params.currency.trim()) || 'BDT',
        });
    };

    const trackSearch = (searchString: string) => {
        if (!searchString || typeof searchString !== 'string') return;
        const trimmed = searchString.trim();
        if (!trimmed) return;

        const now = Date.now();
        // Prevent duplicate calls within 1500ms for exact same query string (e.g. between SearchBar submit and shop.vue watcher)
        if (lastSearchCall.query.toLowerCase() === trimmed.toLowerCase() && now - lastSearchCall.timestamp < 1500) {
            return;
        }
        lastSearchCall = { query: trimmed, timestamp: now };

        track('Search', {
            search_string: trimmed,
        });
    };

    const trackAddToCart = (params: MetaPixelProductEventParams) => {
        const validIds = validateContentIds(params?.content_ids);
        if (validIds.length === 0) return;

        const numericValue = validateNumericValue(params?.value);

        track('AddToCart', {
            content_ids: validIds,
            content_type: 'product',
            content_name: params.content_name || '',
            value: numericValue !== null ? numericValue : 0,
            currency: (params.currency && params.currency.trim()) || 'BDT',
        });
    };

    const trackInitiateCheckout = (params: MetaPixelProductEventParams) => {
        const validIds = validateContentIds(params?.content_ids);
        if (validIds.length === 0) return;

        const numericValue = validateNumericValue(params?.value);
        if (numericValue === null || numericValue < 0) {
            return;
        }

        const now = Date.now();
        const callKey = validIds.slice().sort().join(',') + '_' + numericValue;
        // Debounce only for immediate rapid rerenders (within 1000ms)
        if (lastCheckoutCall.key === callKey && now - lastCheckoutCall.timestamp < 1000) {
            return;
        }
        lastCheckoutCall = { key: callKey, timestamp: now };

        track('InitiateCheckout', {
            content_ids: validIds,
            content_type: 'product',
            value: numericValue,
            currency: (params.currency && params.currency.trim()) || 'BDT',
            num_items: typeof params.num_items === 'number' && params.num_items > 0 ? params.num_items : undefined,
        });
    };

    const trackAddPaymentInfo = (params: MetaPixelProductEventParams) => {
        const validIds = validateContentIds(params?.content_ids);
        if (validIds.length === 0) return;

        const numericValue = validateNumericValue(params?.value);
        if (numericValue === null || numericValue <= 0) {
            return;
        }

        track('AddPaymentInfo', {
            content_ids: validIds,
            content_type: 'product',
            value: numericValue,
            currency: (params.currency && params.currency.trim()) || 'BDT',
        });
    };

    const trackPurchase = (params: MetaPixelPurchaseParams): boolean => {
        if (!isClient) return false;
        if (!params) return false;

        const orderId = String(params.orderId || '').trim();
        if (!orderId) {
            console.warn('[MetaPixel] Suppressing Purchase: missing orderId');
            return false;
        }

        // Validate value: must be positive finite number, do not silently convert to 0
        const numericValue = validateNumericValue(params.value);
        if (numericValue === null || numericValue <= 0) {
            console.warn('[MetaPixel] Suppressing Purchase: invalid or non-positive value', params.value);
            return false;
        }

        // Validate product IDs
        const validIds = validateContentIds(params.content_ids);
        if (validIds.length === 0) {
            console.warn('[MetaPixel] Suppressing Purchase: empty content_ids');
            return false;
        }

        // Check deduplication
        if (isOrderRecorded(orderId)) {
            return false;
        }

        const payload: Record<string, any> = {
            content_ids: validIds,
            content_type: 'product',
            value: numericValue,
            currency: (params.currency && params.currency.trim()) || 'BDT',
        };
        if (typeof params.num_items === 'number' && params.num_items > 0) {
            payload.num_items = params.num_items;
        }

        try {
            dispatch('track', 'Purchase', payload);
            // ONLY record to localStorage AFTER dispatch succeeds without throwing
            recordPurchaseSuccess(orderId);
            return true;
        } catch (err) {
            console.error('[MetaPixel] Failed to dispatch Purchase event:', err);
            return false;
        }
    };

    return {
        track,
        trackCustom,
        trackPageView,
        trackViewContent,
        trackSearch,
        trackAddToCart,
        trackInitiateCheckout,
        trackAddPaymentInfo,
        trackPurchase,
    };
};
